import React, { useMemo } from 'react';

import { Link } from 'react-router-dom';

import { DashboardOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TenantFeatureFlag, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { ISODateStr } from '@recurrency/core-api-schema/dist/common/types';
import { ValidationRuleStatus } from '@recurrency/core-api-schema/dist/validationRules/common';
import { ValidationRuleDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRules';
import moment from 'moment';

import { NewValidationRuleFromTemplateModal } from 'pages/admin/ValidationRules/modals/NewValidationRuleFromTemplateModal';
import { FORECAST_COLOR } from 'pages/purchasing/utils';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { MultiSelectOption } from 'components/AsyncSelect/types';
import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { Button } from 'components/Button';
import { Container } from 'components/Container';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { PageHeader } from 'components/PageHeader';
import { BadgeStatus, StatusBadge } from 'components/recipes/StatusBadge';
import { ResultCount } from 'components/ResultCount';
import { Sparkline } from 'components/Sparkline';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { formatDate } from 'utils/formatting';
import { shouldShowFeatureFlag } from 'utils/roleAndTenant';
import { routes, useHashState } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { asKeyOf } from 'utils/tables';
import { track, TrackEvent } from 'utils/track';

import { ValidationRulesPageHashState } from 'types/hash-state';

import { EditRuleOrTemplateUpdate, UpsertRuleOrTemplateModal } from '../modals/UpsertRuleOrTemplateModal';
import { getCategoryDisplayName } from '../utils';

export const ValidationRulesPage = () => {
  const { activeUser, activeTenant } = useGlobalApp();
  const [hashState, updateHashState] = useHashState<ValidationRulesPageHashState>();
  const tableProps = useCoreApiTableProps({
    schema: schemas.validationRules.getValidationRules,
    queryParams: {
      filter: {
        statuses: hashState.status ? [hashState.status] : undefined,
        isDailyWorkflow: hashState.isDailyWorkflow,
        isOverridesEnabled: hashState.isOverridesEnabled,
      },
    },
  });

  const { reload } = tableProps;

  const treeData = useMemo(() => {
    const grouped: Record<string, ValidationRuleDTO[]> = {};
    for (const item of tableProps.items) {
      const cat = item.category;
      if (!grouped[cat]) {
        grouped[cat] = [];
      }
      grouped[cat].push(item);
    }
    return Object.entries(grouped).map(([category, items]) => ({
      key: `category-${category}`,
      name: getCategoryDisplayName(category),
      isCategory: true as const,
      children: items.map((item) => ({ ...item, key: item.ruleId })),
    }));
  }, [tableProps.items]);

  const showCreateCustomRuleButton = activeUser.isRecurrencyAdmin && activeUser.isRecurrencyInternalUser;

  const showOverrideRules = shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.FeatureOverrideRules);

  const yesNoAllOptions = [
    { label: 'All', value: '' },
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];

  // Currently built for internal use only
  async function handleSubmitCreateRule(updates: EditRuleOrTemplateUpdate) {
    if (!updates) return;

    const ruleName = updates.name || '';
    const deploymentNotificaction = createSubmissionNotification({
      entityName: 'Rule',
      submittingMessage: `Creating rule [${ruleName}]`,
      expectedWaitSeconds: 10,
    });

    try {
      const result = await coreApiFetch(schemas.validationRules.createValidationRule, {
        bodyParams: {
          customRuleConfig: {
            name: updates.name,
            description: updates.description,
            sql: updates.sql,
            isDailyWorkflow: updates.isDailyWorkflow,
            isOverridesEnabled: updates.isOverridesEnabled,
            category: updates.ruleCategory,
          },
        },
      });
      if (result?.status === 200) {
        track(TrackEvent.ValidationRules_CreateCustomRule, {
          ruleId: result.data.ruleId,
          name: updates.name,
          description: updates.description,
          isDailyWorkflow: updates.isDailyWorkflow ?? false,
          isOverridesEnabled: updates.isOverridesEnabled ?? false,
        });
      }
      reload();
    } catch (err) {
      deploymentNotificaction.error(err);
    } finally {
      deploymentNotificaction.success({
        successMessage: `Rule [${ruleName}] was created successfully.`,
      });
    }
  }

  const statusOptions: MultiSelectOption[] = [
    { label: 'All', value: '' },
    { label: 'Deployed', value: ValidationRuleStatus.Deployed },
    { label: 'Draft', value: ValidationRuleStatus.Draft },
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: asKeyOf<ValidationRuleDTO>('name'),
      key: 'ruleName',
      render: (name: string, record: Any) =>
        record.isCategory ? (
          <strong>{name}</strong>
        ) : (
          <Link to={routes.ruleBuilder.ruleDetails(record.ruleId)}>{name}</Link>
        ),
      sorter: true,
    },
    {
      title: 'Last Run Time',
      dataIndex: asKeyOf<ValidationRuleDTO>('lastRunTime'),
      key: 'lastRunTime',
      render: (lastRunTime: ISODateStr, record: Any) =>
        record.isCategory ? null : formatDate(lastRunTime, true, true),
      sorter: true,
    },
    {
      title: 'Items Triggered Last Run',
      key: 'failedRules',
      render: (record: Any) => (record.isCategory ? null : record.runs[0] ? record.runs[0]?.failedItems : 0),
    },
    {
      title: 'Run History',
      key: 'runHistory',
      render: (record: Any) => {
        if (record.isCategory) return null;
        // If there is only 1 data point, duplicate it with 1 second in the future to show a line
        let { runs } = record;
        if (runs.length === 1) {
          const singleRun = runs[0];
          runs = [singleRun, { ...singleRun, runTime: moment(singleRun.runTime).add(1, 'second').toISOString() }];
        }
        return runs.length > 0 ? (
          <Sparkline
            height={50}
            series={[
              {
                data: runs.map((item: Any) => ({
                  date: item.runTime,
                  value: item.failedItems,
                })),
                formatFn: (value: number) => `${value}`,
                title: 'Failed Items',
                color: FORECAST_COLOR,
                showGradient: true,
              },
            ]}
            useDateTime
          />
        ) : (
          <div
            className={css`
              height: 50px;
            `}
          >
            -
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<ValidationRuleDTO>('status'),
      key: 'status',
      render: (status: BadgeStatus, record: Any) => (record.isCategory ? null : <StatusBadge status={status} />),
      sorter: true,
    },

    {
      title: 'Daily Workflow',
      key: 'isDailyWorkflow',
      dataIndex: asKeyOf<ValidationRuleDTO>('isDailyWorkflow'),
      render: (isDailyWorkflow: boolean, record: Any) =>
        record.isCategory ? null : <StatusBadge status={isDailyWorkflow ? BadgeStatus.Yes : BadgeStatus.No} />,
    },
    showOverrideRules
      ? {
          title: 'Override Rule',
          key: 'isOverridesEnabled',
          dataIndex: asKeyOf<ValidationRuleDTO>('isOverridesEnabled'),
          render: (isOverridesEnabled: boolean, record: Any) =>
            record.isCategory ? null : <StatusBadge status={isOverridesEnabled ? BadgeStatus.Yes : BadgeStatus.No} />,
        }
      : null,
  ].filter(truthy);
  return (
    <Container>
      <PageHeader
        title={
          <InfoTooltip placement="bottom" title="Rule Builder allows you to enforce business rules on your data.">
            Rule Builder
          </InfoTooltip>
        }
        headerActions={[
          showCreateCustomRuleButton ? (
            <Button
              key="new-custom-rule-button"
              type="default"
              onClick={async () => {
                await showAsyncModal(UpsertRuleOrTemplateModal, {
                  onSubmit: (newRuleDetails: EditRuleOrTemplateUpdate) => handleSubmitCreateRule(newRuleDetails),
                });
              }}
            >
              New Custom Rule
            </Button>
          ) : null,
          <Button
            key="new-rule-from-template-button"
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionCreateRules]}
            onClick={async () => {
              // open modal
              await showAsyncModal(NewValidationRuleFromTemplateModal, { onClose: () => reload() });
            }}
          >
            <PlusOutlined /> New Rule from Template
          </Button>,
        ]}
      />
      <FilterBarBox dividerLine>
        <FlexSpace direction="column" gap={16} fullWidth>
          <FlexSpace wrap fullWidth>
            <span>Filter</span>
            <AsyncMultiSelect
              label="Status"
              mode="single"
              icon={<DashboardOutlined />}
              selectProps={{ options: statusOptions }}
              selectedValues={[hashState.status || statusOptions[0].value]}
              onSelectedValuesChange={(values) =>
                updateHashState({ status: (values[0] || '') as ValidationRuleStatus })
              }
            />
            <AsyncMultiSelect
              label="Is Daily Workflow"
              mode="single"
              icon={<DashboardOutlined />}
              selectProps={{
                options: yesNoAllOptions,
              }}
              selectedValues={[
                hashState.isDailyWorkflow === undefined ? 'All' : hashState.isDailyWorkflow ? 'Yes' : 'No',
              ]}
              onSelectedValuesChange={(values) =>
                updateHashState({
                  isDailyWorkflow: values[0] === 'Yes' ? true : values[0] === 'No' ? false : undefined,
                })
              }
            />
            {showOverrideRules && (
              <AsyncMultiSelect
                label="Is Override Rule"
                mode="single"
                icon={<SwapOutlined />}
                selectProps={{
                  options: yesNoAllOptions,
                }}
                selectedValues={[
                  hashState.isOverridesEnabled === undefined ? 'All' : hashState.isOverridesEnabled ? 'Yes' : 'No',
                ]}
                onSelectedValuesChange={(values) =>
                  updateHashState({
                    isOverridesEnabled: values[0] === 'Yes' ? true : values[0] === 'No' ? false : undefined,
                  })
                }
              />
            )}
            <FlexSpacer />
            <ResultCount count={tableProps.totalCount} />
          </FlexSpace>
        </FlexSpace>
      </FilterBarBox>

      <AsyncTable
        tableProps={{ ...tableProps, items: treeData, totalCount: treeData.length }}
        columns={columns}
        expandable={{ defaultExpandAllRows: true }}
      />
    </Container>
  );
};
